/**
 * ax-sliders-products — Nosto product source
 *
 * Runs once per <ax-sliders-products data-nosto-placement="..."> on the
 * page. Fetches the configured Nosto placement, parses product handles
 * from the response, and assigns them to the pre-rendered skeleton
 * <nosto-dynamic-card> slots inside the section. The dynamic-card
 * override (assets/nosto-dynamic-card.js) then fetches each
 * /products/{handle}?section_id=card-product-nosto and swaps the
 * skeleton for the real theme card.
 *
 * This file lives separately from sections/ax-sliders-products.liquid
 * because Shopify's section renderer HTML-entity-escapes inline scripts
 * whose contents look HTML-ish (e.g. when they reference custom element
 * names in comments), which silently breaks `&&` → `&amp;&amp;` and
 * `<` → `&lt;`. An external asset is parsed by the browser as plain JS.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const TIMEOUT_MS = 10000;
  const CHECK_INTERVAL = 100;

  function waitForNosto() {
    return new Promise((resolve, reject) => {
      let spent = 0;
      const tick = () => {
        if (typeof window.nostojs === 'function') return resolve();
        if (spent >= TIMEOUT_MS) return reject(new Error('Nosto load timeout'));
        spent += CHECK_INTERVAL;
        setTimeout(tick, CHECK_INTERVAL);
      };
      tick();
    });
  }

  function parseProducts(html) {
    if (!html) return [];
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Path A: Nosto JSON template — script type=application/json data-nosto-recommendation.
    const jsonNode = tmp.querySelector('[data-nosto-recommendation]');
    if (jsonNode) {
      try {
        const parsed = JSON.parse(jsonNode.textContent);
        if (parsed && Array.isArray(parsed.products) && parsed.products.length) {
          return parsed.products;
        }
      } catch (e) {
        console.warn('[ax-sliders-products/nosto] JSON template parse failed:', e);
      }
    }

    // Path B: Velocity / classic HTML template — extract /products/{handle} links.
    const seen = new Set();
    const products = [];
    const links = tmp.querySelectorAll('a[href*="/products/"]');
    for (const link of links) {
      const url = link.getAttribute('href') || '';
      if (!/\/products\//.test(url)) continue;
      const match = url.match(/\/products\/([^/?#]+)/);
      if (!match) continue;
      const handle = match[1];
      if (seen.has(handle)) continue;
      seen.add(handle);
      products.push({
        product_url: url,
        product_id: link.dataset.productId || link.dataset.id || '',
      });
    }
    return products;
  }

  function extractHandle(url) {
    const noQuery = String(url).split('?')[0].replace(/\/+$/, '');
    const parts = noQuery.split('/');
    return parts[parts.length - 1];
  }

  function populate(root, products) {
    const slots = root.querySelectorAll('[data-nosto-slot]');
    for (let i = 0; i < slots.length; i++) {
      const product = products[i];
      if (!product) {
        // Excess slot — once ax-sliders-products has initialised the
        // swiper, the slot div is wrapped inside a swiper-slide; remove
        // the whole wrapper so the carousel doesn't show empty slides.
        // In grid layout there is no swiper-slide wrapper, so we fall
        // back to removing the slot itself.
        const swiperSlide = slots[i].closest('swiper-slide');
        (swiperSlide || slots[i]).remove();
        continue;
      }
      const card = slots[i].querySelector('nosto-dynamic-card');
      if (!card) continue;
      const handle = extractHandle(product.product_url || '');
      if (handle) card.setAttribute('handle', handle);
      slots[i].setAttribute('data-nosto-product-id', product.product_id || '');
    }
    // Tell ax-sliders-products to call swiper.update() so the carousel
    // recomputes widths and peek after the slide set has changed.
    root.dispatchEvent(new CustomEvent('ax-sliders-products:slides-updated', { bubbles: true }));
  }

  // Produits choisis manuellement dans la section, sérialisés en JSON par le Liquid
  // dans l'ATTRIBUT `data-nosto-fallback-products` (pas un <script> inline : le
  // section-renderer Shopify échapperait le contenu du script). getAttribute renvoie
  // la valeur déjà décodée → JSON.parse direct. Sert de repli quand Nosto ne sert rien.
  function readFallback(root) {
    const raw = root.getAttribute('data-nosto-fallback-products');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  // État « Nosto n'a rien servi » : on affiche les produits de repli (manuels) s'il y en a,
  // sinon on masque la section (data-nosto-empty → display:none).
  function emptyState(root, reason) {
    const fallback = readFallback(root);
    if (fallback.length) {
      populate(root, fallback);
      return;
    }
    root.setAttribute('data-nosto-empty', reason);
  }

  async function boot(root) {
    const placement = root.getAttribute('data-nosto-placement');
    if (!placement) return;
    if (root.dataset.nostoBooted === 'true') return;
    root.dataset.nostoBooted = 'true';

    try {
      await waitForNosto();
    } catch (_) {
      emptyState(root, 'nosto-timeout');
      return;
    }

    window.nostojs((api) => {
      const builder = api.createRecommendationRequest({ includeTagging: true }).setElements([placement]);
      // Contexte produit (fiche produit) : on passe le produit courant pour que Nosto
      // recommande AUTOUR de lui (et l'exclue). Sans ça, la reco PDP retombe sur le seul
      // tagging de page — générique/faux si le produit n'est pas (ou mal) taggé.
      const contextProductId = root.getAttribute('data-nosto-context-product-id');
      if (contextProductId) {
        builder.setProducts([{ product_id: String(contextProductId) }]);
      }
      builder
        .load({ skipEvents: true })
        .then((response) => {
          const reco = response && response.recommendations && response.recommendations[placement];
          if (!reco) {
            emptyState(root, 'placement-missing');
            return;
          }
          // Nosto renvoie `recommendations[placement]` soit comme OBJET `{ html, ... }`, soit
          // DIRECTEMENT comme la string HTML (selon la version du SDK). Sur une string,
          // `reco.html` = undefined → 0 produit. On gère les deux formes.
          const recoHtml = typeof reco === 'string' ? reco : reco.html;
          const products = parseProducts(recoHtml);
          if (!products.length) {
            emptyState(root, 'zero-products');
            return;
          }
          populate(root, products);

          // Attribution Nosto des clics « carte → fiche produit ».
          // Les cartes sont chargées en ASYNC par <nosto-dynamic-card>, donc les liens
          // produit n'existent pas encore juste après populate(). On attend que leur
          // nombre se stabilise (toutes chargées) AVANT de lier l'attribution, sinon
          // attributeProductClicksInCampaign ne verrait aucun lien à rattacher.
          if (typeof api.attributeProductClicksInCampaign === 'function') {
            const LINK_SEL = 'a[href*="/products/"]';
            let lastCount = -1;
            let spent = 0;
            const bind = () => {
              const count = root.querySelectorAll(LINK_SEL).length;
              const stable = count > 0 && count === lastCount;
              lastCount = count;
              if (stable || spent >= 6000) {
                if (count > 0) {
                  try {
                    api.attributeProductClicksInCampaign(root, reco);
                  } catch (_) {
                    /* l'attribution clic ne doit jamais casser le rendu */
                  }
                }
                return;
              }
              spent += 300;
              setTimeout(bind, 300);
            };
            setTimeout(bind, 300);
          }
        })
        .catch((err) => {
          console.warn('[ax-sliders-products/nosto] reco request failed:', err);
          emptyState(root, 'api-error');
        });
    });
  }

  function bootAll() {
    const roots = document.querySelectorAll('ax-sliders-products[data-nosto-placement]');
    for (const root of roots) boot(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
